import { zodResolver } from '@hookform/resolvers/zod';
import {
  authControllerForgotPassword,
  rolesControllerFindAll,
  usersControllerAddOverride,
  usersControllerAssignRole,
  usersControllerFindOne,
  usersControllerRemoveOverride,
  usersControllerRemoveRole,
} from '@kalba/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import {
  Activity,
  AlertTriangle,
  Ban,
  Bell,
  Building2,
  Calendar,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  Edit2,
  FileText,
  Home,
  Key,
  Lock,
  Mail,
  Phone,
  Plus,
  Search,
  Shield,
  SlidersHorizontal,
  Trash2,
  UserCheck,
  Users,
  X,
  XCircle,
  Zap,
} from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { AppLayout } from '../../../components/layout/app-layout';
import { GradientAvatar } from '../../../components/shared/gradient-avatar';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Tabs } from '../../../components/ui/tabs';
import { usePermissions } from '../../../hooks/use-permissions';
import { cn } from '../../../lib/utils';
import { useAuthStore } from '../../../stores/auth.store';

// ── Route ─────────────────────────────────────────────────────────────────────

export const Route = createFileRoute('/users/$userId/')({
  beforeLoad: () => {
    const { user, permissions } = useAuthStore.getState();
    if (!user) throw redirect({ to: '/login' });
    if (!permissions.includes('user:view')) throw redirect({ to: '/dashboard' });
  },
  component: UserDetailPage,
});

// ── Types ─────────────────────────────────────────────────────────────────────

interface UserRole {
  id: string;
  name: string;
  isSystem: boolean;
}

interface PermissionOverride {
  id: string;
  permissionId: string;
  permissionKey: string;
  type: 'GRANT' | 'REVOKE';
  reason?: string | null;
}

interface UserDetail {
  id: string;
  name: string;
  email: string;
  status: string;
  roles: UserRole[];
  permissionOverrides: PermissionOverride[];
}

interface Role {
  id: string;
  name: string;
  isSystem: boolean;
}

// ── Static mock (no API endpoint for these) ───────────────────────────────────

type ModulePermission = {
  id: string;
  name: string;
  desc: string;
  source: string;
  sourceType: 'role' | 'override';
  state: 'granted' | 'revoked';
};
type Module = {
  id: string;
  label: string;
  icon: React.ElementType;
  grantedCount: number;
  totalCount: number;
  permissions: ModulePermission[];
};

const MODULES: Module[] = [
  {
    id: 'customers',
    label: 'Customers',
    icon: Users,
    grantedCount: 4,
    totalCount: 5,
    permissions: [
      {
        id: 'c1',
        name: 'View Customers',
        desc: 'Browse customer list and view account details',
        source: 'Admin',
        sourceType: 'role',
        state: 'granted',
      },
      {
        id: 'c2',
        name: 'Create Customer',
        desc: 'Add new customer accounts to the system',
        source: 'Admin',
        sourceType: 'role',
        state: 'granted',
      },
      {
        id: 'c3',
        name: 'Edit Customer',
        desc: 'Modify customer information and contacts',
        source: 'Admin',
        sourceType: 'role',
        state: 'granted',
      },
      {
        id: 'c4',
        name: 'Export Customers',
        desc: 'Download customer data as CSV or PDF',
        source: 'Direct Override',
        sourceType: 'override',
        state: 'granted',
      },
      {
        id: 'c5',
        name: 'Delete Customer',
        desc: 'Permanently remove customer records',
        source: 'Direct Override',
        sourceType: 'override',
        state: 'revoked',
      },
    ],
  },
  {
    id: 'permits',
    label: 'Permits',
    icon: FileText,
    grantedCount: 6,
    totalCount: 6,
    permissions: [
      {
        id: 'p1',
        name: 'View Permits',
        desc: 'Browse all permit applications and statuses',
        source: 'Permits Manager',
        sourceType: 'role',
        state: 'granted',
      },
      {
        id: 'p2',
        name: 'Create Permit',
        desc: 'Submit new permit applications',
        source: 'Permits Manager',
        sourceType: 'role',
        state: 'granted',
      },
      {
        id: 'p3',
        name: 'Edit Permit',
        desc: 'Modify existing permit details and documents',
        source: 'Permits Manager',
        sourceType: 'role',
        state: 'granted',
      },
      {
        id: 'p4',
        name: 'Approve Permit',
        desc: 'Grant final approval for permit applications',
        source: 'Admin',
        sourceType: 'role',
        state: 'granted',
      },
      {
        id: 'p5',
        name: 'Reject Permit',
        desc: 'Deny permit applications with reason',
        source: 'Admin',
        sourceType: 'role',
        state: 'granted',
      },
      {
        id: 'p6',
        name: 'Print Permit',
        desc: 'Generate and print official permit documents',
        source: 'Permits Manager',
        sourceType: 'role',
        state: 'granted',
      },
    ],
  },
  {
    id: 'users',
    label: 'Users',
    icon: Users,
    grantedCount: 4,
    totalCount: 5,
    permissions: [
      {
        id: 'u1',
        name: 'View Users',
        desc: 'Browse all system users and their profiles',
        source: 'Admin',
        sourceType: 'role',
        state: 'granted',
      },
      {
        id: 'u2',
        name: 'Create User',
        desc: 'Invite and onboard new system users',
        source: 'Admin',
        sourceType: 'role',
        state: 'granted',
      },
      {
        id: 'u3',
        name: 'Edit User',
        desc: 'Update user profiles, roles, and settings',
        source: 'Admin',
        sourceType: 'role',
        state: 'granted',
      },
      {
        id: 'u4',
        name: 'Manage Roles',
        desc: 'Assign and revoke roles from users',
        source: 'Admin',
        sourceType: 'role',
        state: 'granted',
      },
      {
        id: 'u5',
        name: 'Delete User',
        desc: 'Permanently remove user accounts',
        source: 'Direct Override',
        sourceType: 'override',
        state: 'revoked',
      },
    ],
  },
  {
    id: 'config',
    label: 'Configuration',
    icon: SlidersHorizontal,
    grantedCount: 2,
    totalCount: 4,
    permissions: [
      {
        id: 'cfg1',
        name: 'View Configuration',
        desc: 'Read system settings and configurations',
        source: 'Admin',
        sourceType: 'role',
        state: 'granted',
      },
      {
        id: 'cfg2',
        name: 'Edit Configuration',
        desc: 'Modify system-wide settings',
        source: 'Admin',
        sourceType: 'role',
        state: 'granted',
      },
      {
        id: 'cfg3',
        name: 'Manage Integrations',
        desc: 'Configure third-party integrations',
        source: 'Direct Override',
        sourceType: 'override',
        state: 'revoked',
      },
      {
        id: 'cfg4',
        name: 'Billing Settings',
        desc: 'Access and modify billing configuration',
        source: 'Direct Override',
        sourceType: 'override',
        state: 'revoked',
      },
    ],
  },
  {
    id: 'notifications',
    label: 'Notifications',
    icon: Bell,
    grantedCount: 3,
    totalCount: 3,
    permissions: [
      {
        id: 'n1',
        name: 'View Notifications',
        desc: 'Receive and read system notifications',
        source: 'Admin',
        sourceType: 'role',
        state: 'granted',
      },
      {
        id: 'n2',
        name: 'Send Notifications',
        desc: 'Broadcast messages to other users',
        source: 'Admin',
        sourceType: 'role',
        state: 'granted',
      },
      {
        id: 'n3',
        name: 'Manage Alerts',
        desc: 'Configure automated alert thresholds',
        source: 'Direct Override',
        sourceType: 'override',
        state: 'granted',
      },
    ],
  },
];

const ACTIVITY = [
  {
    icon: Activity,
    label: 'Signed in',
    detail: 'Chrome · macOS Ventura',
    time: 'Today 9:24 AM',
    color: 'text-primary',
    bg: 'bg-primary/10',
  },
  {
    icon: Shield,
    label: 'Role assigned: Permits Mgr',
    detail: 'By Aisha Malik',
    time: 'Mar 20, 2024',
    color: 'text-violet-600',
    bg: 'bg-violet-100',
  },
  {
    icon: Lock,
    label: 'Permission override added',
    detail: 'Delete Customer → Revoked',
    time: 'Mar 18, 2024',
    color: 'text-red-500',
    bg: 'bg-red-50',
  },
  {
    icon: Activity,
    label: 'Signed in',
    detail: 'Safari · iPhone 15 Pro',
    time: 'Mar 17, 2024',
    color: 'text-primary',
    bg: 'bg-primary/10',
  },
  {
    icon: Key,
    label: 'Password changed',
    detail: 'Self-initiated',
    time: 'Mar 10, 2024',
    color: 'text-amber-600',
    bg: 'bg-amber-100',
  },
  {
    icon: Shield,
    label: 'Role assigned: Admin',
    detail: 'By Elena Rodriguez',
    time: 'Jan 15, 2023',
    color: 'text-violet-600',
    bg: 'bg-violet-100',
  },
  {
    icon: UserCheck,
    label: 'Account created',
    detail: 'Invited by John Doe',
    time: 'Jan 12, 2023',
    color: 'text-teal-600',
    bg: 'bg-teal-50',
  },
];

// ── Status config ─────────────────────────────────────────────────────────────

const STATUS_CFG: Record<
  string,
  { label: string; dot: string; color: string; bg: string; border: string }
> = {
  ACTIVE: {
    label: 'Active',
    dot: 'bg-success animate-pulse',
    color: 'text-success-700',
    bg: 'bg-success-50',
    border: 'border-success-200',
  },
  INACTIVE: {
    label: 'Inactive',
    dot: 'bg-gray-400',
    color: 'text-gray-600',
    bg: 'bg-gray-100',
    border: 'border-gray-200',
  },
  SUSPENDED: {
    label: 'Suspended',
    dot: 'bg-red-500',
    color: 'text-red-700',
    bg: 'bg-red-50',
    border: 'border-red-200',
  },
  INVITED: {
    label: 'Invited',
    dot: 'bg-blue-400',
    color: 'text-blue-700',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
  },
};

// ── Role colors ───────────────────────────────────────────────────────────────

const ROLE_COLORS = [
  {
    color: 'text-violet-700',
    bg: 'bg-violet-50',
    border: 'border-violet-200',
    dot: 'bg-violet-500',
  },
  { color: 'text-primary', bg: 'bg-primary/8', border: 'border-primary/25', dot: 'bg-primary' },
  { color: 'text-blue-700', bg: 'bg-blue-50', border: 'border-blue-200', dot: 'bg-blue-500' },
  { color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200', dot: 'bg-amber-500' },
  { color: 'text-teal-700', bg: 'bg-teal-50', border: 'border-teal-200', dot: 'bg-teal-500' },
];

function getRoleColor(index: number) {
  return ROLE_COLORS[index % ROLE_COLORS.length];
}

// ── Override form schema ───────────────────────────────────────────────────────

const overrideSchema = z.object({
  permissionId: z.string().min(1, 'Select a permission'),
  type: z.enum(['GRANT', 'REVOKE']),
  reason: z.string().optional(),
});

type OverrideFormValues = z.infer<typeof overrideSchema>;

// ── Tab definitions ───────────────────────────────────────────────────────────

const TABS = (grantedCount: number) => [
  { id: 'permissions', label: `Permissions (${grantedCount})` },
  { id: 'overview', label: 'Overview' },
  { id: 'activity', label: 'Activity' },
];

// ── Page ──────────────────────────────────────────────────────────────────────

function UserDetailPage() {
  const { userId } = Route.useParams();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canManageOverrides = can('user:manage-overrides');
  const qc = useQueryClient();

  const [activeTab, setActiveTab] = useState('permissions');
  const [expandedModules, setExpandedModules] = useState<Set<string>>(
    new Set(['customers', 'permits', 'users']),
  );
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);
  const [roleSearch, setRoleSearch] = useState('');
  const [showOverrideForm, setShowOverrideForm] = useState(false);
  const [confirmAction, setConfirmAction] = useState<'suspend' | 'delete' | null>(null);

  const {
    register: registerOverride,
    handleSubmit: handleOverrideSubmit,
    watch: watchOverride,
    setValue: setOverrideValue,
    reset: resetOverrideForm,
    formState: { errors: overrideErrors },
  } = useForm<OverrideFormValues>({
    resolver: zodResolver(overrideSchema),
    defaultValues: { permissionId: '', type: 'GRANT', reason: '' },
  });

  const overrideType = watchOverride('type');

  const {
    data: user,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['user', userId],
    queryFn: () => usersControllerFindOne({ id: userId }) as unknown as Promise<UserDetail>,
  });

  const { data: allRoles = [] } = useQuery({
    queryKey: ['roles'],
    queryFn: () => rolesControllerFindAll() as unknown as Promise<Role[]>,
    enabled: can('role:view'),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['user', userId] });

  const assignRoleMutation = useMutation({
    mutationFn: (roleId: string) =>
      usersControllerAssignRole({ id: userId, requestBody: { roleId } }),
    onSuccess: () => {
      invalidate();
      setShowRoleDropdown(false);
      setRoleSearch('');
    },
  });

  const removeRoleMutation = useMutation({
    mutationFn: (roleId: string) => usersControllerRemoveRole({ id: userId, roleId }),
    onSuccess: invalidate,
  });

  const addOverrideMutation = useMutation({
    mutationFn: (data: OverrideFormValues) =>
      usersControllerAddOverride({
        id: userId,
        requestBody: {
          permissionId: data.permissionId,
          type: data.type,
          reason: data.reason || undefined,
        },
      }),
    onSuccess: () => {
      invalidate();
      setShowOverrideForm(false);
      resetOverrideForm();
    },
  });

  const removeOverrideMutation = useMutation({
    mutationFn: (permId: string) =>
      usersControllerRemoveOverride({ id: userId, permissionId: permId }),
    onSuccess: invalidate,
  });

  const resetPasswordMutation = useMutation({
    mutationFn: () => authControllerForgotPassword({ requestBody: { email: user!.email } }),
    onSuccess: () =>
      toast.success('Password reset email sent', {
        description: `A reset link has been sent to ${user?.email}`,
      }),
    onError: () => toast.error('Failed to send reset email'),
  });

  const assignedRoleIds = new Set(user?.roles.map((r) => r.id) ?? []);
  const availableRoles = allRoles.filter((r) => !assignedRoleIds.has(r.id));
  const filteredAvailable = availableRoles.filter((r) =>
    r.name.toLowerCase().includes(roleSearch.toLowerCase()),
  );

  const totalGranted = MODULES.reduce((acc, m) => acc + m.grantedCount, 0);
  const totalPerms = MODULES.reduce((acc, m) => acc + m.totalCount, 0);
  const overrideCount = MODULES.flatMap((m) => m.permissions).filter(
    (p) => p.sourceType === 'override',
  ).length;

  const toggleModule = (id: string) => {
    setExpandedModules((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const sc = STATUS_CFG[user?.status ?? ''] ?? STATUS_CFG.INACTIVE;

  // ── Loading / error ────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center py-24">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      </AppLayout>
    );
  }

  if (isError || !user) {
    return (
      <AppLayout>
        <div className="rounded-2xl border border-red-200 bg-red-50 p-8 text-center">
          <p className="text-sm font-medium text-red-600">User not found.</p>
        </div>
      </AppLayout>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <AppLayout noPadding>
      <div className="p-6 md:p-8 space-y-6 pb-16">
        {/* ── Breadcrumb ── */}
        <nav className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <button
            type="button"
            onClick={() => navigate({ to: '/dashboard' })}
            className="shrink-0 transition-colors hover:text-foreground"
          >
            <Home size={12} />
          </button>
          <ChevronRight size={12} className="shrink-0" />
          <button
            type="button"
            onClick={() => navigate({ to: '/users' })}
            className="hover:text-foreground transition-colors"
          >
            Users
          </button>
          <ChevronRight size={12} className="shrink-0" />
          <span className="text-foreground font-medium">{user.name}</span>
        </nav>

        {/* ── Hero card ── */}
        <div className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
          <div className="h-1.5 bg-gradient-to-r from-violet-500 via-purple-500 to-indigo-500" />
          <div className="p-7">
            <div className="flex items-start gap-6">
              {/* Avatar */}
              <GradientAvatar
                name={user.name}
                size="lg"
                shape="rounded"
                className="w-16 h-16 text-xl shadow-lg shrink-0"
              />

              {/* Info + actions */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  {/* Name + status */}
                  <div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <h1 className="text-2xl font-bold tracking-tight">{user.name}</h1>
                      <span
                        className={cn(
                          'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border',
                          sc.bg,
                          sc.color,
                          sc.border,
                        )}
                      >
                        <span className={cn('w-1.5 h-1.5 rounded-full', sc.dot)} />
                        {sc.label}
                      </span>
                    </div>
                    <p className="text-muted-foreground text-sm mt-0.5">Operations · EMP-00124</p>
                  </div>

                  {/* Action buttons */}
                  <div className="shrink-0">
                    {confirmAction === 'suspend' ? (
                      <div className="flex items-center gap-2 bg-warning/10 border border-warning/30 rounded-xl p-3">
                        <AlertTriangle className="w-4 h-4 text-warning shrink-0" />
                        <span className="text-sm font-medium text-warning">Suspend this user?</span>
                        <button
                          type="button"
                          onClick={() => setConfirmAction(null)}
                          className="px-3 py-1.5 rounded-lg text-xs font-semibold text-muted-foreground hover:bg-accent transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmAction(null)}
                          className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-warning text-white hover:bg-warning/90 transition-colors"
                        >
                          Confirm
                        </button>
                      </div>
                    ) : confirmAction === 'delete' ? (
                      <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 rounded-xl p-3">
                        <AlertTriangle className="w-4 h-4 text-red-500 shrink-0" />
                        <span className="text-sm font-medium text-red-500">
                          Delete permanently?
                        </span>
                        <button
                          type="button"
                          onClick={() => setConfirmAction(null)}
                          className="px-3 py-1.5 rounded-lg text-xs font-semibold text-muted-foreground hover:bg-accent transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmAction(null)}
                          className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-500 text-white hover:bg-red-600 transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() =>
                            navigate({ to: '/users/$userId/edit', params: { userId } })
                          }
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-success text-white text-sm font-semibold hover:bg-success/90 transition-all shadow-md shadow-success/20"
                        >
                          <Edit2 className="w-3.5 h-3.5" /> Edit User
                        </button>
                        <button
                          type="button"
                          onClick={() => resetPasswordMutation.mutate()}
                          disabled={resetPasswordMutation.isPending}
                          className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border border-border text-sm font-medium text-muted-foreground hover:bg-accent hover:text-foreground transition-all disabled:opacity-50"
                        >
                          <Key className="w-3.5 h-3.5" /> Reset Password
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmAction('suspend')}
                          className="p-2 rounded-xl border border-border text-muted-foreground hover:bg-warning/10 hover:text-warning hover:border-warning/30 transition-all"
                          title="Suspend user"
                        >
                          <Ban className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmAction('delete')}
                          className="p-2 rounded-xl border border-border text-muted-foreground hover:bg-red-500/10 hover:text-red-500 hover:border-red-500/30 transition-all"
                          title="Delete user"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Contact row */}
                <div className="flex items-center gap-5 mt-3 flex-wrap">
                  <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                    <Mail className="w-3.5 h-3.5 shrink-0" />
                    <span>{user.email}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                    <Phone className="w-3.5 h-3.5 shrink-0" />
                    <span>+1 (415) 555-0142</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                    <Building2 className="w-3.5 h-3.5 shrink-0" />
                    <span>Operations</span>
                  </div>
                </div>

                {/* Meta pills */}
                <div className="flex items-center gap-2.5 mt-4 flex-wrap">
                  {[
                    { icon: Clock, label: 'Last login', value: 'Today at 9:24 AM' },
                    { icon: Calendar, label: 'Joined', value: 'Jan 12, 2023' },
                    {
                      icon: Zap,
                      label: 'Permissions',
                      value: `${totalGranted}/${totalPerms} granted`,
                    },
                    { icon: Shield, label: 'Roles', value: `${user.roles.length} assigned` },
                  ].map(({ icon: Icon, label, value }, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-1.5 text-xs text-muted-foreground bg-muted/50 rounded-lg px-3 py-1.5 border border-border/60"
                    >
                      <Icon className="w-3 h-3 shrink-0" />
                      <span>
                        {label}: <span className="font-semibold text-foreground">{value}</span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Two-column grid ── */}
        <div className="grid grid-cols-3 gap-6 items-start">
          {/* Left: tabs + content (2/3) */}
          <div className="col-span-2 space-y-5">
            {/* Tab nav */}
            <Tabs tabs={TABS(totalGranted)} activeTab={activeTab} onTabChange={setActiveTab} />

            {/* ── Permissions tab ── */}
            {activeTab === 'permissions' && (
              <div className="space-y-3">
                {/* Override form card — only for actors who can grant/revoke overrides */}
                {canManageOverrides && (
                  <div className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setShowOverrideForm((v) => !v)}
                      className="w-full flex items-center gap-4 p-5 hover:bg-accent/30 transition-colors"
                    >
                      <div className="w-9 h-9 rounded-xl bg-teal-50 flex items-center justify-center shrink-0">
                        <Lock className="w-4 h-4 text-teal-600" />
                      </div>
                      <div className="flex-1 text-left">
                        <p className="font-semibold text-sm">Permission Overrides</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Grant or revoke specific permissions directly on this user
                        </p>
                      </div>
                      <div className="flex items-center gap-2.5 shrink-0">
                        <span className="px-2.5 py-1 bg-teal-50 text-teal-700 border border-teal-200 text-xs font-semibold rounded-full">
                          {overrideCount} active
                        </span>
                        <ChevronDown
                          className={cn(
                            'w-4 h-4 text-muted-foreground transition-transform duration-200',
                            showOverrideForm && 'rotate-180',
                          )}
                        />
                      </div>
                    </button>

                    {showOverrideForm && (
                      <form
                        onSubmit={handleOverrideSubmit((data) => addOverrideMutation.mutate(data))}
                        className="border-t border-border p-5 space-y-4 bg-muted/20"
                      >
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-1.5">
                            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                              Permission
                            </label>
                            <div className="relative">
                              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                              <Input
                                {...registerOverride('permissionId')}
                                placeholder="Search permissions…"
                                className="pl-9"
                              />
                            </div>
                            {overrideErrors.permissionId && (
                              <p className="text-xs text-error-600">
                                {overrideErrors.permissionId.message}
                              </p>
                            )}
                          </div>
                          <div className="space-y-1.5">
                            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                              Action
                            </label>
                            <div className="flex gap-2">
                              {(['GRANT', 'REVOKE'] as const).map((action) => (
                                <button
                                  key={action}
                                  type="button"
                                  onClick={() => setOverrideValue('type', action)}
                                  className={cn(
                                    'flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-sm font-semibold border transition-all',
                                    overrideType === action
                                      ? action === 'GRANT'
                                        ? 'bg-success-500 text-white border-success-500 shadow-md shadow-success-500/20'
                                        : 'bg-error-500 text-white border-error-500 shadow-md shadow-error-500/20'
                                      : 'border-gray-300 text-gray-500 hover:bg-gray-50',
                                  )}
                                >
                                  {action === 'GRANT' ? (
                                    <CheckCircle2 className="w-3.5 h-3.5" />
                                  ) : (
                                    <XCircle className="w-3.5 h-3.5" />
                                  )}
                                  {action === 'GRANT' ? 'Grant' : 'Revoke'}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                        <div className="space-y-1.5">
                          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                            Reason (optional)
                          </label>
                          <Input
                            {...registerOverride('reason')}
                            placeholder="Add a reason for this override…"
                          />
                        </div>
                        <div className="flex justify-end gap-2 pt-1">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setShowOverrideForm(false);
                              resetOverrideForm();
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            type="submit"
                            variant={overrideType === 'GRANT' ? 'success' : 'destructive'}
                            size="sm"
                            disabled={addOverrideMutation.isPending}
                          >
                            Apply Override
                          </Button>
                        </div>
                      </form>
                    )}
                  </div>
                )}

                {/* Module accordions */}
                {MODULES.map((mod) => {
                  const ModIcon = mod.icon;
                  const isOpen = expandedModules.has(mod.id);
                  const pct = Math.round((mod.grantedCount / mod.totalCount) * 100);
                  return (
                    <div
                      key={mod.id}
                      className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden transition-shadow hover:shadow-md"
                    >
                      <button
                        type="button"
                        onClick={() => toggleModule(mod.id)}
                        className="w-full flex items-center gap-4 p-5 hover:bg-accent/20 transition-colors"
                      >
                        <div className="w-9 h-9 rounded-xl bg-muted/60 flex items-center justify-center shrink-0">
                          <ModIcon className="w-4 h-4 text-muted-foreground" />
                        </div>
                        <div className="flex-1 text-left min-w-0">
                          <div className="flex items-center gap-3">
                            <span className="font-semibold text-sm">{mod.label}</span>
                            <span className="text-xs text-muted-foreground">
                              {mod.grantedCount} of {mod.totalCount} granted
                            </span>
                          </div>
                          <div className="flex items-center gap-2.5 mt-2">
                            <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden max-w-36">
                              <div
                                className={cn(
                                  'h-full rounded-full transition-all duration-500',
                                  pct === 100
                                    ? 'bg-success'
                                    : pct >= 60
                                      ? 'bg-primary'
                                      : 'bg-warning',
                                )}
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                            <span
                              className={cn(
                                'text-xs font-semibold',
                                pct === 100
                                  ? 'text-success'
                                  : pct >= 60
                                    ? 'text-primary'
                                    : 'text-warning',
                              )}
                            >
                              {pct}%
                            </span>
                          </div>
                        </div>
                        <ChevronDown
                          className={cn(
                            'w-4 h-4 text-muted-foreground transition-transform duration-200 shrink-0',
                            isOpen && 'rotate-180',
                          )}
                        />
                      </button>

                      {isOpen && (
                        <div className="border-t border-border divide-y divide-border/50">
                          {mod.permissions.map((perm) => (
                            <div
                              key={perm.id}
                              className={cn(
                                'flex items-start gap-4 px-5 py-4 transition-colors',
                                perm.state === 'granted'
                                  ? 'hover:bg-success/[0.03]'
                                  : 'opacity-65 hover:bg-red-500/[0.03]',
                              )}
                            >
                              <div
                                className={cn(
                                  'w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5',
                                  perm.state === 'granted' ? 'bg-success/10' : 'bg-red-500/10',
                                )}
                              >
                                {perm.state === 'granted' ? (
                                  <CheckCircle2 className="w-4 h-4 text-success" />
                                ) : (
                                  <XCircle className="w-4 h-4 text-red-500" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span
                                    className={cn(
                                      'text-sm font-semibold',
                                      perm.state === 'revoked' &&
                                        'line-through text-muted-foreground',
                                    )}
                                  >
                                    {perm.name}
                                  </span>
                                  {perm.sourceType === 'override' && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-teal-50 text-teal-700 border border-teal-200 rounded-full text-[11px] font-semibold">
                                      <Zap className="w-2.5 h-2.5" /> Override
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                                  {perm.desc}
                                </p>
                              </div>
                              <div className="shrink-0 flex items-center gap-2">
                                <span
                                  className={cn(
                                    'text-[11px] font-medium px-2 py-1 rounded-lg border',
                                    perm.sourceType === 'role'
                                      ? 'bg-muted/60 text-muted-foreground border-border'
                                      : 'bg-teal-50 text-teal-700 border-teal-200',
                                  )}
                                >
                                  {perm.sourceType === 'role' ? perm.source : 'Direct Override'}
                                </span>
                                {perm.sourceType === 'override' && canManageOverrides && (
                                  <button
                                    type="button"
                                    onClick={() => removeOverrideMutation.mutate(perm.id)}
                                    disabled={removeOverrideMutation.isPending}
                                    className="p-1 rounded-lg hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors disabled:opacity-50"
                                  >
                                    <X className="w-3.5 h-3.5" />
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* ── Overview tab ── */}
            {activeTab === 'overview' && (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  {[
                    {
                      label: 'Assigned Roles',
                      value: user.roles.length,
                      icon: Shield,
                      color: 'text-violet-600',
                      bg: 'bg-violet-50',
                      border: 'border-violet-200',
                    },
                    {
                      label: 'Permissions Granted',
                      value: totalGranted,
                      icon: CheckCircle2,
                      color: 'text-success',
                      bg: 'bg-success/10',
                      border: 'border-success/20',
                    },
                    {
                      label: 'Direct Overrides',
                      value: overrideCount,
                      icon: Lock,
                      color: 'text-teal-600',
                      bg: 'bg-teal-50',
                      border: 'border-teal-200',
                    },
                  ].map(({ label, value, icon: Icon, color, bg, border }, i) => (
                    <div
                      key={i}
                      className={cn(
                        'bg-card rounded-2xl border shadow-sm p-5 hover:shadow-md transition-all',
                        border,
                      )}
                    >
                      <div
                        className={cn(
                          'w-10 h-10 rounded-xl flex items-center justify-center mb-3',
                          bg,
                        )}
                      >
                        <Icon className={cn('w-5 h-5', color)} />
                      </div>
                      <div className="text-3xl font-bold tracking-tight">{value}</div>
                      <div className="text-sm text-muted-foreground mt-1">{label}</div>
                    </div>
                  ))}
                </div>

                <div className="bg-card rounded-2xl border border-border shadow-sm p-5">
                  <h3 className="font-semibold text-sm mb-5">Permission Coverage by Module</h3>
                  <div className="space-y-4">
                    {MODULES.map((mod) => {
                      const pct = Math.round((mod.grantedCount / mod.totalCount) * 100);
                      const ModIcon = mod.icon;
                      return (
                        <div key={mod.id} className="flex items-center gap-4">
                          <div className="w-8 h-8 rounded-xl bg-muted/60 flex items-center justify-center shrink-0">
                            <ModIcon className="w-3.5 h-3.5 text-muted-foreground" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-1.5">
                              <span className="text-sm font-medium">{mod.label}</span>
                              <span className="text-xs text-muted-foreground">
                                {mod.grantedCount}/{mod.totalCount}
                              </span>
                            </div>
                            <div className="h-2 bg-muted rounded-full overflow-hidden">
                              <div
                                className={cn(
                                  'h-full rounded-full transition-all duration-500',
                                  pct === 100
                                    ? 'bg-success'
                                    : pct >= 60
                                      ? 'bg-primary'
                                      : 'bg-warning',
                                )}
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                          </div>
                          <span
                            className={cn(
                              'text-xs font-bold w-10 text-right',
                              pct === 100
                                ? 'text-success'
                                : pct >= 60
                                  ? 'text-primary'
                                  : 'text-warning',
                            )}
                          >
                            {pct}%
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* ── Activity tab ── */}
            {activeTab === 'activity' && (
              <div className="bg-card rounded-2xl border border-border shadow-sm p-6">
                <h3 className="font-semibold text-sm mb-6">Full Activity History</h3>
                <div className="relative pl-5">
                  <div className="absolute left-4 top-3 bottom-3 w-px bg-border" />
                  <div className="space-y-0">
                    {ACTIVITY.map((item, i) => {
                      const Icon = item.icon;
                      return (
                        <div
                          key={i}
                          className="relative flex items-start gap-4 pb-6 last:pb-0 group"
                        >
                          <div
                            className={cn(
                              'w-9 h-9 rounded-xl flex items-center justify-center shrink-0 relative z-10 ring-2 ring-card group-hover:scale-105 transition-transform',
                              item.bg,
                            )}
                          >
                            <Icon className={cn('w-4 h-4', item.color)} />
                          </div>
                          <div className="flex-1 min-w-0 pt-1">
                            <p className="text-sm font-semibold">{item.label}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">{item.detail}</p>
                          </div>
                          <span className="text-xs text-muted-foreground pt-1 shrink-0">
                            {item.time}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right sidebar (1/3) */}
          <div className="space-y-5 sticky top-24">
            {/* Assigned Roles */}
            <div className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
              <div className="p-5 border-b border-border">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-sm">Assigned Roles</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {user.roles.length} role{user.roles.length !== 1 ? 's' : ''} assigned
                    </p>
                  </div>
                  {can('role:assign-permissions') && (
                    <button
                      type="button"
                      onClick={() => setShowRoleDropdown((v) => !v)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-success text-white text-xs font-semibold hover:bg-success/90 transition-all shadow-sm shadow-success/20"
                    >
                      <Plus className="w-3.5 h-3.5" /> Add Role
                    </button>
                  )}
                </div>

                {showRoleDropdown && filteredAvailable.length > 0 && (
                  <div className="mt-3 space-y-2 animate-in slide-in-from-top-1 duration-150">
                    <div className="relative">
                      <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                      <Input
                        value={roleSearch}
                        onChange={(e) => setRoleSearch(e.target.value)}
                        placeholder="Search roles…"
                        autoFocus
                        className="pl-9 py-2 h-9"
                      />
                    </div>
                    <div className="space-y-0.5 max-h-40 overflow-y-auto">
                      {filteredAvailable.map((r) => (
                        <button
                          key={r.id}
                          type="button"
                          onClick={() => assignRoleMutation.mutate(r.id)}
                          disabled={assignRoleMutation.isPending}
                          className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl hover:bg-accent transition-colors text-left group disabled:opacity-50"
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">{r.name}</span>
                            <span
                              className={cn(
                                'text-[11px] font-semibold px-1.5 py-0.5 rounded-full',
                                r.isSystem
                                  ? 'bg-violet-100 text-violet-700'
                                  : 'bg-primary/10 text-primary',
                              )}
                            >
                              {r.isSystem ? 'system' : 'custom'}
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="p-3 space-y-2">
                {user.roles.length === 0 ? (
                  <div className="py-6 text-center">
                    <Shield className="w-8 h-8 text-muted-foreground mx-auto mb-2 opacity-30" />
                    <p className="text-xs text-muted-foreground">No roles assigned</p>
                  </div>
                ) : (
                  user.roles.map((role, i) => {
                    const rc = getRoleColor(i);
                    return (
                      <Link
                        key={role.id}
                        to="/users/roles/$roleId"
                        params={{ roleId: role.id }}
                        className={cn(
                          'flex items-start gap-3 p-3.5 rounded-xl border group hover:shadow-sm transition-all cursor-pointer',
                          rc.border,
                          rc.bg,
                        )}
                      >
                        <span className={cn('w-2 h-2 rounded-full mt-1.5 shrink-0', rc.dot)} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className={cn('text-sm font-semibold', rc.color)}>
                              {role.name}
                            </span>
                            <span
                              className={cn(
                                'text-[11px] font-semibold px-1.5 py-0.5 rounded-full border',
                                role.isSystem
                                  ? 'bg-muted/80 text-muted-foreground border-border'
                                  : 'bg-primary/15 text-primary border-primary/30',
                              )}
                            >
                              {role.isSystem ? 'system' : 'custom'}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                            Role with access controls
                          </p>
                        </div>
                        {can('role:assign-permissions') && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              removeRoleMutation.mutate(role.id);
                            }}
                            disabled={removeRoleMutation.isPending}
                            className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg hover:bg-red-500/10 hover:text-red-500 text-muted-foreground transition-all shrink-0 disabled:opacity-30"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </Link>
                    );
                  })
                )}
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-card rounded-2xl border border-border shadow-sm p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-sm">Recent Activity</h3>
                <button
                  type="button"
                  onClick={() => setActiveTab('activity')}
                  className="text-xs text-primary font-semibold hover:text-primary/80 transition-colors"
                >
                  View all
                </button>
              </div>
              <div className="space-y-3">
                {ACTIVITY.slice(0, 4).map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <div key={i} className="flex items-start gap-3 group">
                      <div
                        className={cn(
                          'w-7 h-7 rounded-lg flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform',
                          item.bg,
                        )}
                      >
                        <Icon className={cn('w-3.5 h-3.5', item.color)} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold truncate">{item.label}</p>
                        <p className="text-[11px] text-muted-foreground mt-0.5">{item.time}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
